import { Box, Checkbox, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { Fragment, useEffect, useMemo, useState } from 'react'

import { useBookingContext } from '@providers/booking'
import { TBookingPet } from '@providers/booking/types'
import { formatNumberAsPrice } from '@shared/common'
import { i18WithDefault as t } from '@shared/locale'
import { addonGroupsByCategory } from '@shared/pet_addons'
import { Types } from 'service-api'
import { DiscountTag } from 'components-ui'

export const PetCard = ({
  petIdx,
  pet,
  groupIdx,
  groom,
  togglePetInGroom,
  addons,
  searchGroupIdx,
}: {
  petIdx: number
  pet: TBookingPet
  groupIdx: number
  groom: Types.BookingSearchClientDto
  togglePetInGroom: (petIdx: number) => void
  addons: Types.BookingSearchClientDto[]
  searchGroupIdx: number
}) => {
  const bookingCtx = useBookingContext()
  const theme = useTheme()
  const [isSelected, setIsSelected] = useState<boolean>(true)

  useEffect(() => {
    const currentPetGroom = bookingCtx.state.cart.groups[groupIdx].grooms[petIdx]?.filter(
      (g) => g.offer.id === groom.offer?.id
    )
    setIsSelected(currentPetGroom?.[0]?.offer.id ? true : false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCtx.state.cart.groups[groupIdx].grooms[petIdx], groupIdx])

  const addonGroups = useMemo(() => addonGroupsByCategory(addons), [addons])

  const toggleAddon = (addon: Types.BookingSearchAdminDto) => {
    bookingCtx.dispatch({ type: 'TOGGLE_GROOM_ADDON', payload: { value: { ...addon, pet }, groupIdx, petIdx } })
  }

  return (
    <Box
      sx={{
        maxWidth: '640px',
        px: 2,
        py: 0.5,
        margin: '0 auto',
        width: '100%',
        backgroundColor: theme.palette!.primary?.shade5,
      }}
    >
      <Stack key={`group-${pet.id}`} direction="row" alignItems="center" spacing={3}>
        <Typography variant="body2" flexGrow={1}>
          {pet.name}
        </Typography>
        {groom.availabilityGroups?.[searchGroupIdx]?.availabilities?.[0].price?.rate && (
          <>{formatNumberAsPrice(groom.availabilityGroups?.[searchGroupIdx].availabilities?.[0].price?.rate)}</>
        )}
        <Stack direction="row" justifyContent="center" alignItems="center">
          <Tooltip
            title={isSelected ? t('addonItemPicker.removeItem') : t('addonItemPicker.addItem')}
            disableInteractive
          >
            <IconButton
              onClick={() => {
                togglePetInGroom(petIdx)
              }}
              sx={{ p: 0 }}
            >
              <Checkbox sx={{ py: 0.5, ml: 0 }} data-testid="selected-check" checked={isSelected} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      {addonGroups &&
        addonGroups.order.map((og) => {
          const addOnsToRender = addonGroups.items[og]
          return (
            <Fragment key={addOnsToRender[0].offer?.id}>
              <Typography marginX={2} variant="body2" color={theme.palette.text.secondary}>
                {og}
              </Typography>
              {addOnsToRender.map((addon) => {
                const currentPetAddons = bookingCtx.state.cart.groups[groupIdx].groomAddOn?.[petIdx] || []
                const isAddonSelected: boolean = currentPetAddons.some((g) => g.offer.id === addon.offer?.id) || false
                return (
                  <Stack key={addon.offer?.id} direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <IconButton
                      onClick={() => {
                        toggleAddon(addon)
                      }}
                      sx={{ p: '6px' }}
                    >
                      <Checkbox
                        sx={{ py: 0.5, ml: 0 }}
                        data-testid="selected-check"
                        checked={isAddonSelected}
                        disabled={!addon.availabilityGroups?.some((ag) => ag.availabilities?.some((a) => a.available))}
                      />
                    </IconButton>
                    <Typography component="span" sx={{ minWidth: 0 }}>
                      {addon.offer?.displayName}
                      {' ('}
                      {addon.availabilityGroups?.[searchGroupIdx].availabilities?.[0].price?.rate && (
                        <>
                          {addon.offer?.priceVaries && `${t('offers.startingAt')}${' '}`}
                          {formatNumberAsPrice(
                            addon.availabilityGroups?.[searchGroupIdx].availabilities?.[0].price?.rate
                          )}
                        </>
                      )}
                      {')'}
                    </Typography>
                    <DiscountTag
                      discounts={addon.availabilityGroups?.[searchGroupIdx]?.availabilities?.[0]?.price?.discounts}
                      cssOverrides={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                        color: 'text.primary',
                        '& .MuiChip-label': {
                          color: 'inherit',
                        },
                        '& .MuiChip-icon': {
                          fill: 'action.active !important',
                        },
                      }}
                      iconColor="action.active"
                      eligibleForConsumableBenefits={addon.eligibleForConsumableBenefits}
                    />
                  </Stack>
                )
              })}
            </Fragment>
          )
        })}
    </Box>
  )
}
